import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, ArrowRight, Building2, ScrollText, Wrench, CheckCircle2 } from "lucide-react";
import { LOCAL_CITIES } from "../data/citiesRhoneAlpes";

/**
 * Landing page locale dynamique — /pharmacie/:city
 * Contenu SEO longue traîne "croix pharmacie <ville>" par entrée dans citiesRhoneAlpes.js.
 * Réutilisation du même layout ; contenu 100 % localisé.
 */

const Section = ({ eyebrow, title, subtitle, children, id, testid }) => (
  <section
    id={id}
    className="py-14 lg:py-20 border-b border-white/5"
    data-testid={testid || `section-${id}`}
  >
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
      {eyebrow && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#39FF14]" style={{ boxShadow: "0 0 12px #39FF14" }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
            {eyebrow}
          </span>
        </div>
      )}
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase leading-tight tracking-tight text-white">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-base text-zinc-400 max-w-3xl font-body leading-relaxed">
          {subtitle}
        </p>
      )}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

const Chip = ({ children }) => (
  <span className="inline-block px-3 py-1.5 border border-white/10 bg-white/[0.03] text-zinc-300 font-mono text-[11px] uppercase tracking-wider hover:border-[#39FF14]/40 hover:text-[#39FF14] transition-colors">
    {children}
  </span>
);

const BulletList = ({ items, testid }) => (
  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5 text-zinc-300 font-body text-base leading-relaxed" data-testid={testid}>
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2 w-1.5 h-1.5 bg-[#39FF14] rounded-full shrink-0" style={{ boxShadow: "0 0 8px #39FF14" }} />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function LocalCityLanding() {
  const { city } = useParams();
  const data = LOCAL_CITIES[city];

  useEffect(() => {
    if (!data) return;
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content");
    document.title = data.seoTitle;
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.setAttribute("name", "description");
      document.head.appendChild(desc);
    }
    desc.setAttribute("content", data.metaDescription);
    return () => {
      document.title = prevTitle || "Signapharma France";
      if (prevDesc) desc.setAttribute("content", prevDesc);
    };
  }, [data]);

  if (!data) {
    return <Navigate to="/enseigne-pharmacie-france" replace />;
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid={`page-pharmacie-${data.slug}`}>
      {/* HERO */}
      <section className="pt-28 lg:pt-32 pb-16 border-b border-white/5 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#39FF14 1px, transparent 1px), linear-gradient(90deg, #39FF14 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 relative">
          <div className="flex items-center gap-3 mb-5">
            <MapPin size={16} className="text-[#39FF14]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
              {data.departement} ({data.departementNumber}) · {data.region}
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] tracking-tight max-w-4xl">
            <span className="chrome-text">Croix de pharmacie</span>
            <span className="block mt-1">et enseignes lumineuses à</span>
            <span className="block mt-2 text-[#39FF14] neon-text-soft">{data.city}</span>
          </h1>
          <p className="mt-6 text-base lg:text-lg text-zinc-300 max-w-3xl font-body leading-relaxed">
            {data.subheading}
          </p>
          {data.intro.map((p, i) => (
            <p key={i} className="mt-4 text-base text-zinc-400 max-w-3xl font-body leading-relaxed">
              {p}
            </p>
          ))}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-neon" data-testid="cta-devis-city-hero">
              <span className="flex items-center gap-2">Demander un devis {data.city} <ArrowRight size={16} /></span>
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border border-white/20 text-white font-mono text-xs uppercase tracking-[0.2em] hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
              data-testid="cta-photos-city-hero"
            >
              Envoyer les photos de ma façade
            </Link>
            <Link
              to="/galerie"
              className="px-6 py-3 text-zinc-400 font-mono text-xs uppercase tracking-[0.2em] hover:text-[#39FF14] transition-colors"
            >
              Découvrir nos réalisations →
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <Section
        id="services"
        eyebrow={`Services · ${data.city}`}
        title={`Nos prestations pour les pharmacies de ${data.city}`}
        subtitle="Solutions sur mesure adaptées à la façade, à la charte graphique de l'officine et aux règles locales d'urbanisme."
        testid="section-services"
      >
        <BulletList items={data.services} testid="list-services" />
      </Section>

      {/* QUARTIERS & COMMUNES */}
      <Section
        id="zone"
        eyebrow="Zone d'intervention"
        title={`Quartiers de ${data.city} et communes desservies`}
        subtitle={`Nous intervenons sur tous les quartiers de ${data.city} et les communes du bassin ${data.departement.toLowerCase()}.`}
        testid="section-zone"
      >
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="label-tech text-[#39FF14] mb-4 flex items-center gap-2">
              <Building2 size={14} /> Quartiers de {data.city}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="list-districts">
              {data.districts.map((d, i) => <Chip key={i}>{d}</Chip>)}
            </div>
          </div>
          <div>
            <div className="label-tech text-[#39FF14] mb-4 flex items-center gap-2">
              <MapPin size={14} /> Communes voisines desservies
            </div>
            <div className="flex flex-wrap gap-2" data-testid="list-surrounding">
              {data.surroundingCities.map((c, i) => <Chip key={i}>{c}</Chip>)}
            </div>
          </div>
        </div>
      </Section>

      {/* CADRE LOCAL */}
      <Section
        id="cadre-local"
        eyebrow="Cadre légal local"
        title={`Réglementation à ${data.city}`}
        subtitle="Chaque commune peut apporter des prescriptions complémentaires au Code de l'environnement — notre étude en tient compte."
        testid="section-cadre-local"
      >
        <ul className="space-y-4 text-zinc-300 font-body text-base leading-relaxed" data-testid="list-local-specifics">
          {data.localSpecifics.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <ScrollText size={18} className="text-[#39FF14] mt-0.5 shrink-0" />
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* PROCESS */}
      <Section
        id="process"
        eyebrow="Notre méthode"
        title="Étude, fabrication et pose"
        subtitle="Un accompagnement de bout en bout, de l'idée à la mise en service — chaque étape est précisée dans le devis."
        testid="section-process"
      >
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { i: "01", t: "Étude gratuite", d: `Vous nous transmettez des photos de la façade à ${data.city}, les dimensions approximatives et votre logo. Nous préparons une simulation visuelle et un devis complet.` },
            { i: "02", t: "Fabrication sur mesure", d: `Fabrication dans notre atelier de Saint-Jean-de-Gonville (${data.departementNumber === "01" ? "même département" : "Ain, 01"}). Choix des dimensions, du texte, de la couleur LED, de la finition et du type de pose.` },
            { i: "03", t: "Livraison & pose", d: `Livraison à ${data.city} et mise en service. Pose, nacelle, déplacement, raccordement électrique et SAV précisés séparément dans chaque devis.` },
          ].map(({ i, t, d }) => (
            <div key={i} className="p-6 border border-white/10 bg-white/[0.02] hover:border-[#39FF14]/40 transition-colors" data-testid={`process-step-${i}`}>
              <div className="font-mono text-xs uppercase tracking-widest text-[#39FF14] mb-3">{i}</div>
              <h3 className="font-display text-lg font-semibold uppercase text-white mb-3">{t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA FINAL */}
      <section className="py-20 lg:py-24 relative overflow-hidden" data-testid="section-cta-final">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ background: "radial-gradient(circle at 50% 50%, #39FF14 0%, transparent 60%)" }}
        />
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center relative">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 border border-[#39FF14]/40 bg-[#39FF14]/10">
            <CheckCircle2 size={14} className="text-[#39FF14]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#39FF14]">
              Interventions {data.city} · {data.departement}
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase leading-tight tracking-tight text-white">
            Votre <span className="text-[#39FF14] neon-text-soft">croix de pharmacie LED</span><br className="hidden sm:block" /> à {data.city} ?
          </h2>
          <p className="mt-6 text-base lg:text-lg text-zinc-400 font-body max-w-2xl mx-auto">
            Envoyez les photos de votre façade — nous vous préparons une étude adaptée à votre officine et à la réglementation locale de {data.city}.
          </p>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-4">
            <Link to="/contact" className="btn-neon" data-testid="cta-devis-city-final">
              <span className="flex items-center gap-2">Demander un devis <ArrowRight size={16} /></span>
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border border-white/20 text-white font-mono text-xs uppercase tracking-[0.2em] hover:border-[#39FF14] hover:text-[#39FF14] transition-colors"
              data-testid="cta-photos-city-final"
            >
              Envoyer les photos de ma façade
            </Link>
            <Link
              to="/enseigne-pharmacie-france"
              className="px-6 py-3 text-zinc-400 font-mono text-xs uppercase tracking-[0.2em] hover:text-[#39FF14] transition-colors"
            >
              Voir tous nos services →
            </Link>
          </div>
          <p className="mt-14 text-xs text-zinc-600 font-body max-w-2xl mx-auto italic leading-relaxed">
            Intervention et livraison partout en France selon la nature du projet. Pose, déplacement, moyens d'accès et SAV précisés dans le devis.
          </p>

          {/* Lien vers les autres villes */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <div className="label-tech text-zinc-500 mb-4">
              <Wrench size={12} className="inline mr-1" /> Autres villes desservies
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {Object.values(LOCAL_CITIES)
                .filter((c) => c.slug !== data.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/pharmacie/${c.slug}`}
                    data-testid={`link-other-city-${c.slug}`}
                    className="px-4 py-2 border border-white/10 hover:border-[#39FF14]/60 text-zinc-300 hover:text-[#39FF14] font-mono text-xs uppercase tracking-widest transition-colors"
                  >
                    Pharmacie {c.city} →
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
